import React, { useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiX, FiCheck, FiLoader, FiImage, FiTrash2, FiEye, FiCopy, FiAlertCircle } = FiIcons;

const PhotoUpload = ({ onPhotoSelect, selectedPhoto = null, showGallery = true }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const fileInputRef = useRef(null);

  // 🔥 AKTIVIERTER UPLOAD: Lade Fotos aus der Datenbank
  const loadPhotos = async () => {
    setLoadingPhotos(true);
    try {
      const { data, error } = await supabase
        .from('menu_photos_a7b9c8d6e5')
        .select('*')
        .eq('is_active', true)
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.log('Photo table error:', error);
        setPhotos([]);
        return;
      }

      setPhotos(data || []);
    } catch (err) {
      console.log('Error loading photos:', err);
      setPhotos([]);
    } finally {
      setLoadingPhotos(false);
    }
  };

  // Load photos when component mounts or gallery opens
  React.useEffect(() => {
    if (showGallery) {
      loadPhotos();
    }
  }, [showGallery]);

  // 🔥 AKTIVIERTER UPLOAD: Foto-Upload-Funktion
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const file = files[0];
      console.log('🔥 Starting file upload:', file.name, file.size, file.type);

      // Validiere Datei
      if (!file.type.startsWith('image/')) {
        throw new Error('Nur Bilddateien sind erlaubt');
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB
        throw new Error('Datei ist zu groß (max. 5MB)');
      }

      // Erstelle eindeutigen Dateinamen
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileExtension = file.name.split('.').pop();
      const fileName = `menu_${timestamp}_${randomString}.${fileExtension}`;
      const filePath = `2025/${fileName}`;

      console.log('🔥 Upload path:', filePath);

      // Upload zu Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('menu-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('🔥 Upload error:', uploadError);
        throw new Error(`Upload-Fehler: ${uploadError.message}`);
      }

      console.log('🔥 Upload successful:', uploadData);

      // Hole die öffentliche URL
      const { data: { publicUrl } } = supabase.storage
        .from('menu-photos')
        .getPublicUrl(filePath);

      console.log('🔥 Public URL:', publicUrl);

      // Speichere Metadaten in der Datenbank
      const { data: dbData, error: dbError } = await supabase
        .from('menu_photos_a7b9c8d6e5')
        .insert({
          filename: fileName,
          original_filename: file.name,
          file_path: filePath,
          public_url: publicUrl,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: 'admin',
          is_active: true,
          description: `Uploaded: ${file.name}`,
          tags: ['menu', 'upload']
        })
        .select()
        .single();

      if (dbError) {
        console.error('🔥 Database error:', dbError);
        throw new Error(`Datenbank-Fehler: ${dbError.message}`);
      }

      console.log('🔥 Database entry created:', dbData);

      setSuccess(`Foto "${file.name}" erfolgreich hochgeladen!`);
      setUploadProgress(100);
      
      // Foto automatisch auswählen
      if (onPhotoSelect) {
        onPhotoSelect(publicUrl);
      }

      // Fotos neu laden
      await loadPhotos();

      // Erfolg-Nachricht nach 3 Sekunden ausblenden
      setTimeout(() => {
        setSuccess('');
        setUploadProgress(0);
      }, 3000);

    } catch (err) {
      console.error('🔥 Upload failed:', err);
      setError(err.message || 'Unbekannter Fehler beim Upload');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle drag and drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleUpload(Array.from(files));
    }
  }, []);

  // Handle file input change
  const handleFileInput = (e) => {
    const files = e.target?.files;
    if (files && files.length > 0) {
      handleUpload(Array.from(files));
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('URL kopiert!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Fehler beim Kopieren der URL');
    }
  };

  // Delete photo
  const deletePhoto = async (photo) => {
    if (!confirm(`Foto "${photo.original_filename}" wirklich löschen?`)) return;
    
    try {
      // Lösche aus Storage
      const { error: storageError } = await supabase.storage
        .from('menu-photos')
        .remove([photo.file_path]);

      if (storageError) {
        console.error('Storage delete error:', storageError);
      }

      // Lösche aus Datenbank
      const { error: dbError } = await supabase
        .from('menu_photos_a7b9c8d6e5')
        .delete()
        .eq('id', photo.id);

      if (dbError) {
        throw new Error(`Fehler beim Löschen: ${dbError.message}`);
      }

      setSuccess('Foto erfolgreich gelöscht!');
      await loadPhotos();
      setTimeout(() => setSuccess(''), 2000);

    } catch (err) {
      setError(err.message || 'Fehler beim Löschen');
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
          <SafeIcon icon={FiUpload} className="mr-2 text-primary-600" />
          Foto-Upload
        </h3>

        {/* Status Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center">
            <SafeIcon icon={FiAlertCircle} className="mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center">
            <SafeIcon icon={FiCheck} className="mr-2" />
            {success}
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Upload läuft...</span>
              <span className="text-sm text-gray-500">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary-500 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Upload Area */}
        <div 
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
            dragActive 
              ? 'border-primary-400 bg-primary-50' 
              : 'border-gray-300 bg-gray-50 hover:border-primary-300 hover:bg-primary-25'
          } ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !isUploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="hidden"
            disabled={isUploading}
          />
          
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              {isUploading ? (
                <SafeIcon icon={FiLoader} className="text-2xl text-primary-600 animate-spin" />
              ) : (
                <SafeIcon icon={FiImage} className="text-2xl text-primary-600" />
              )}
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-700 mb-2">
                {isUploading ? 'Upload läuft...' : 'Foto hochladen'}
              </h4>
              <p className="text-gray-500 text-sm mb-4">
                Ziehen Sie eine Bilddatei hierher oder klicken Sie zum Auswählen
              </p>
              <p className="text-xs text-gray-400">
                Unterstützte Formate: JPG, PNG, WebP, GIF (max. 5MB)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Gallery */}
      {showGallery && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center">
              <SafeIcon icon={FiImage} className="mr-2 text-primary-600" />
              Foto-Galerie ({photos.length})
            </h3>
            <div className="flex space-x-2">
              <motion.button
                onClick={loadPhotos}
                disabled={loadingPhotos}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <SafeIcon icon={loadingPhotos ? FiLoader : FiLoader} className={loadingPhotos ? 'animate-spin' : ''} />
              </motion.button>

              <motion.button
                onClick={() => setShowPhotoGallery(!showPhotoGallery)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 bg-primary-100 text-primary-600 rounded-lg hover:bg-primary-200 transition-colors"
              >
                <SafeIcon icon={showPhotoGallery ? FiX : FiEye} />
              </motion.button>
            </div>
          </div>

          {showPhotoGallery && (
            <div>
              {loadingPhotos ? (
                <div className="flex items-center justify-center py-8">
                  <SafeIcon icon={FiLoader} className="animate-spin text-2xl text-primary-600 mr-3" />
                  <span className="text-gray-600">Fotos werden geladen...</span>
                </div>
              ) : photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <motion.div
                      key={photo.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className={`relative group rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                        selectedPhoto === photo.public_url
                          ? 'border-primary-500 ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      <div className="aspect-square">
                        <img
                          src={photo.public_url}
                          alt={photo.original_filename}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => onPhotoSelect && onPhotoSelect(photo.public_url)}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/300x300?text=Bild+nicht+verfügbar';
                          }}
                        />
                      </div>

                      {/* Overlay with actions */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => onPhotoSelect && onPhotoSelect(photo.public_url)}
                            className="p-2 bg-white text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                            title="Foto auswählen"
                          >
                            <SafeIcon icon={FiCheck} />
                          </motion.button>

                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => copyToClipboard(photo.public_url)}
                            className="p-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                            title="URL kopieren"
                          >
                            <SafeIcon icon={FiCopy} />
                          </motion.button>

                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => deletePhoto(photo)}
                            className="p-2 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                            title="Foto löschen"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </motion.button>
                        </div>
                      </div>

                      {/* Selection indicator */}
                      {selectedPhoto === photo.public_url && (
                        <div className="absolute top-2 right-2 bg-primary-500 text-white p-1 rounded-full">
                          <SafeIcon icon={FiCheck} className="text-xs" />
                        </div>
                      )}

                      {/* File info */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                        <p className="text-white text-xs truncate">{photo.original_filename}</p>
                        <p className="text-white text-xs opacity-75">
                          {(photo.file_size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <SafeIcon icon={FiImage} className="mx-auto text-4xl mb-4 opacity-50" />
                  <p>Noch keine Fotos hochgeladen</p>
                  <p className="text-sm mt-2">Laden Sie Ihr erstes Foto hoch!</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;